import React, { useEffect, useMemo, useState } from 'react';
import { ChevronRight, Folder as FolderIcon, HardDrive } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import type { FileFolder } from '../types';
import { buildFolderTree, type TreeNode } from './FileTree';

interface MoveDialogProps {
  open: boolean;
  folders: FileFolder[];
  excludeFolderIds?: string[];
  currentFolderId?: string | null;
  isSubmitting?: boolean;
  onClose: () => void;
  onMove: (destination: string | null) => Promise<void> | void;
}

export const MoveDialog: React.FC<MoveDialogProps> = ({
  open,
  folders,
  excludeFolderIds = [],
  currentFolderId = null,
  isSubmitting = false,
  onClose,
  onMove,
}) => {
  const [destination, setDestination] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const excluded = useMemo(() => {
    const set = new Set<string>(excludeFolderIds);
    const byParent = new Map<string | null, FileFolder[]>();
    for (const folder of folders) {
      const key = folder.parent ?? null;
      const list = byParent.get(key) ?? [];
      list.push(folder);
      byParent.set(key, list);
    }
    const collect = (id: string) => {
      const directChildren = byParent.get(id) ?? [];
      for (const child of directChildren) {
        if (!set.has(child.id)) {
          set.add(child.id);
          collect(child.id);
        }
      }
    };
    for (const id of excludeFolderIds) collect(id);
    return set;
  }, [folders, excludeFolderIds]);

  useEffect(() => {
    if (open) {
      setDestination(null);
      setError(null);
    }
  }, [open]);

  const tree = useMemo(() => buildFolderTree(folders), [folders]);

  const handleSubmit = async () => {
    if (destination === currentFolderId) {
      setError('Pick a different folder than the current one.');
      return;
    }
    setError(null);
    try {
      await onMove(destination);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to move items.');
    }
  };

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <Dialog open={open} onOpenChange={(value) => (!value ? onClose() : undefined)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Move to...</DialogTitle>
          <DialogDescription>Choose a destination folder.</DialogDescription>
        </DialogHeader>

        <div className="max-h-72 overflow-y-auto rounded-md border p-2">
          <button
            type="button"
            onClick={() => setDestination(null)}
            className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors ${
              destination === null
                ? 'bg-accent text-accent-foreground font-medium'
                : 'hover:bg-accent/60'
            }`}
          >
            <HardDrive className="h-4 w-4 text-muted-foreground" />
            <span>All Files (root)</span>
          </button>
          <ul className="space-y-px">
            {tree.children.map((node) => (
              <DestinationRow
                key={node.folder!.id}
                node={node}
                depth={1}
                excluded={excluded}
                expanded={expanded}
                destination={destination}
                onToggle={toggle}
                onSelect={setDestination}
              />
            ))}
          </ul>
        </div>

        {error && <p className="text-xs text-destructive">{error}</p>}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Moving...' : 'Move'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface DestinationRowProps {
  node: TreeNode;
  depth: number;
  excluded: Set<string>;
  expanded: Set<string>;
  destination: string | null;
  onToggle: (id: string) => void;
  onSelect: (id: string) => void;
}

const DestinationRow: React.FC<DestinationRowProps> = ({
  node,
  depth,
  excluded,
  expanded,
  destination,
  onToggle,
  onSelect,
}) => {
  const folder = node.folder!;
  const isDisabled = excluded.has(folder.id);
  const hasChildren = node.children.length > 0;
  const isExpanded = expanded.has(folder.id);
  const isActive = destination === folder.id;

  return (
    <li>
      <div
        className={`flex items-center gap-1 rounded-md px-1 py-1 text-sm transition-colors ${
          isActive
            ? 'bg-accent text-accent-foreground font-medium'
            : isDisabled
              ? 'opacity-40'
              : 'hover:bg-accent/60'
        }`}
        style={{ paddingLeft: `${depth * 12}px` }}
      >
        <button
          type="button"
          onClick={() => hasChildren && onToggle(folder.id)}
          className={`flex h-5 w-5 items-center justify-center rounded text-muted-foreground transition-transform ${
            hasChildren ? 'hover:text-foreground' : 'invisible'
          } ${isExpanded ? 'rotate-90' : ''}`}
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          disabled={isDisabled}
          onClick={() => !isDisabled && onSelect(folder.id)}
          className="flex flex-1 items-center gap-2 truncate text-left disabled:cursor-not-allowed"
        >
          <FolderIcon className="h-4 w-4 text-primary shrink-0" />
          <span className="truncate">{folder.name}</span>
        </button>
      </div>
      {isExpanded && hasChildren && (
        <ul className="space-y-px">
          {node.children.map((child) => (
            <DestinationRow
              key={child.folder!.id}
              node={child}
              depth={depth + 1}
              excluded={excluded}
              expanded={expanded}
              destination={destination}
              onToggle={onToggle}
              onSelect={onSelect}
            />
          ))}
        </ul>
      )}
    </li>
  );
};
